import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { MODES } from "@/modes";
import { CreateGameDialog } from "@/components/teacher/CreateGameDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EMPTY_FILTER, ListFilter, Pager, useFiltered, usePaged } from "@/components/teacher/ListFilter";
import { AccessBadge, ShareButton, ShareDialog } from "@/components/teacher/ShareDialog";
import { LikeButton, TagChips, TagDialog } from "@/components/teacher/Social";
import { PacksSection } from "@/components/teacher/PacksSection";
import { Showcase } from "@/components/teacher/Showcase";
import { LockButton, LockedBadge } from "@/components/teacher/SiteAdmin";
import { useAuth } from "@/lib/auth";
import { useCatalog } from "@/lib/catalog";
import { ContentBadges, trackView } from "@/components/teacher/ContentBadges";

const STATUS = { lobby: ["Lobi", "text-[#FACC15]"], running: ["Sürüyor", "text-[#6EE7B7]"], paused: ["Duraklatıldı", "text-amber-400"], finished: ["Bitti", "text-slate-400"] };
const roomText = (r) => `${r.code} ${MODES[r.mode]?.label || r.mode} ${r.pool_names.join(" ")}`;
const previewRoom = async (code) => {
  try {
    const { data } = await api.post("/preview", { room_code: code });
    window.open(`/onizleme/${data.code}`, "_blank");
  } catch (e) { toast.error(errMsg(e)); }
};

export default function GamesPage() {
  useCatalog();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [sharing, setSharing] = useState(null);
  const [tagging, setTagging] = useState(null);
  const shown = useFiltered(rooms, filter, roomText);
  const pg = usePaged(shown, 6);
  const dev = Boolean(useAuth().teacher?.developer);

  const load = useCallback(() => {
    api.get("/rooms").then((r) => setRooms(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try { await api.delete(`/rooms/${toDelete.code}`); toast.success("Oyun ve verileri silindi"); load(); } catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1">
          <p className="eyebrow">Canlı oyunlar</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-1">Oyunlar</h1>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="h-12 px-6 text-base font-black" data-testid="create-game-button"><Plus className="h-5 w-5 mr-2" />Yeni oyun</Button>
      </div>
      <ListFilter items={rooms} value={filter} onChange={setFilter} placeholder="Oyun kodu, havuz ya da öğretmen ara…" />
      {shown.length === 0 ? <div className="panel p-10 text-slate-400" data-testid="games-empty">{rooms.length ? "Aramaya uygun oyun yok." : "Henüz oyun yok."}</div> : (
        <div className="space-y-3" data-testid="games-list">
          {pg.items.map((r) => (
            <div key={r.id} className={`panel p-5 flex flex-wrap items-center gap-4 hover:bg-white/10 hover:border-[#FACC15]/60 transition-[background-color,border-color] duration-200 cursor-pointer ${r.locked ? "opacity-60" : ""}`} onClick={() => navigate(`/ogretmen/oyun/${r.code}`)} data-testid={`game-row-${r.code}`}>
              <span className="font-display text-3xl font-black text-[#FACC15] tracking-widest">{r.code}</span>
              <div className="flex-1 min-w-[200px]">
                <p className="font-bold">{MODES[r.mode]?.label || r.mode}</p>
                <p className="text-sm text-slate-400">{r.pool_names.join(", ")} · {r.student_count} öğrenci · {new Date(r.created_at).toLocaleString("tr-TR")}</p>
                <p className="text-xs text-slate-500 mt-0.5" data-testid={`game-owner-${r.code}`}>Oluşturan: <span className="text-slate-300 font-semibold">{r.owner_name}</span> <AccessBadge item={r} /> <LockedBadge item={r} /></p>
                <div className="flex items-center gap-2 mt-1" onClick={(e) => e.stopPropagation()}><LikeButton kind="rooms" item={r} /><TagChips item={r} /><ContentBadges kind="rooms" item={r} onChange={load} /></div>
              </div>
              <span className={`font-bold ${STATUS[r.status]?.[1]}`}>{STATUS[r.status]?.[0]}</span>
              <ShareButton item={r} onClick={setSharing} />
              <LockButton kind="rooms" item={r} onChange={load} />
              <Button size="sm" variant="outline" disabled={r.locked && !dev} onClick={(e) => { e.stopPropagation(); trackView("rooms", r.id); previewRoom(r.code); }} data-testid={`game-preview-${r.code}`}><Eye className="h-4 w-4 mr-1" />Önizle</Button>
              {r.mine && <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setTagging(r); }} data-testid={`game-tag-${r.code}`}>Öğrenim düzeyi / etiket</Button>}
              {r.mine && <button onClick={(e) => { e.stopPropagation(); setToDelete(r); }} className="p-2 rounded hover:bg-red-900/40 text-red-400" data-testid={`game-delete-${r.code}`} aria-label="Oyunu sil">
                <Trash2 className="h-5 w-5" />
              </button>}
            </div>
          ))}
        </div>
      )}
      <Pager pg={pg} testId="games-pager" />
      <Showcase kind="rooms" items={rooms} onOpen={(r) => navigate(`/ogretmen/oyun/${r.code}`)} />
      <PacksSection kind="rooms" items={rooms} onEditContent={(p) => navigate(`/ogretmen/oyun/${p.code}`)} />
      <CreateGameDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ShareDialog kind="rooms" item={sharing} onOpenChange={(o) => !o && setSharing(null)} onSaved={load} />
      <TagDialog kind="rooms" item={tagging} onOpenChange={(o) => !o && setTagging(null)} onSaved={load} />
      <ConfirmDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)} title="Oyun silinsin mi?"
        description={`${toDelete?.code} kodlu oyun ve sınıf rekoru kayıtları silinecek. Analizleri korunur; istersen Analiz sayfasından ayrıca silebilirsin.`} onConfirm={remove} />
    </div>
  );
}
